<template>
	<v-dialog v-model="open" :attach="overlayTarget" transition="dialog-bottom-transition" class="points-page-dialog">
		<v-card class="points-page">
			<v-card-title class="headline">
				<md-icon>paid</md-icon>
				{{ t('MyPoints') }}
			</v-card-title>

			<v-card-text class="points-page__body">
				<div v-if="loading" class="points-page__loading">
					<v-progress-circular indeterminate color="warning" />
				</div>

				<template v-else>
					<section class="points-page__summary">
						<div class="points-page__org">
							<span>{{ user.organization?.parent }}</span>
							<span class="points-page__org-sep">·</span>
							<span>{{ user.organization?.own }}</span>
							<v-spacer />
							<!-- Tier chip (e.g. "Gold") temporarily hidden — to re-enable later: remove v-if="false" -->
							<v-chip v-if="false" :class="['points-page__tier', tierClass(user.tier)]" size="small" variant="flat">
								<md-icon class="points-page__tier-icon">military_tech</md-icon>
								{{ user.tier }}
							</v-chip>
						</div>

						<div class="points-page__total">{{ formatNumber(user.total) }}</div>
						<div class="points-page__total-sub">{{ t('PointsThisSeason') }}</div>

						<v-chip v-if="user.rank" class="points-page__rank" variant="tonal" color="warning" size="default">
							<md-icon>emoji_events</md-icon>
							{{ user.rank }}
							<sup>e</sup>
							{{ t('Of') }} {{ user.totalUsers }} — {{ t('SeeRanking') }} ›
						</v-chip>

						<v-alert v-if="user.nextRank" class="points-page__hint" type="info" variant="tonal" density="compact" icon="local_fire_department">
							{{ t('PointsToReach') }}
							<strong>{{ user.nextRank.pointsToReach }} {{ t('pts') }}</strong>
							{{ t('ForPosition') }} {{ user.nextRank.position }}
							<sup>e</sup>
						</v-alert>

						<!-- "Next tier" progress bar temporarily hidden — to re-enable later: restore v-if="user.nextTier" -->
						<div v-if="false" class="points-page__tier-progress">
							<div class="points-page__tier-progress-head">
								<span>
									<md-icon>military_tech</md-icon>
									{{ t('PointsToReach') }}
									<strong>{{ user.nextTier.pointsToReach }} {{ t('pts') }}</strong>
									{{ t('ForTier') }} {{ user.nextTier.name }}
								</span>
								<span class="points-page__tier-progress-pct">{{ user.nextTier.progressPct }}%</span>
							</div>
							<v-progress-linear :model-value="user.nextTier.progressPct" color="warning" height="6" rounded />
						</div>

						<div class="points-page__kpis">
							<div class="points-page__kpi">
								<div class="points-page__kpi-value points-page__kpi-value--pending">{{ formatNumber(user.pending) }}</div>
								<div class="points-page__kpi-label">{{ t('Pending') }}</div>
							</div>
							<div class="points-page__kpi">
								<div class="points-page__kpi-value points-page__kpi-value--validated">{{ formatNumber(user.validated) }}</div>
								<div class="points-page__kpi-label">{{ t('Validated') }}</div>
							</div>
							<div class="points-page__kpi">
								<div class="points-page__kpi-value points-page__kpi-value--denied">{{ formatNumber(user.denied) }}</div>
								<div class="points-page__kpi-label">{{ t('Denied') }}</div>
							</div>
						</div>
					</section>

					<section v-if="user.season" class="points-page__season">
						<v-icon>emoji_events</v-icon>
						<div class="points-page__season-text">
							<div class="points-page__season-name">{{ t('Season') }} {{ user.season.name }}</div>
							<div v-if="user.season.daysRemaining != null" class="points-page__season-sub">{{ user.season.daysRemaining }} {{ t('DaysRemaining') }}</div>
						</div>
					</section>

					<section class="points-page__filters">
						<v-text-field v-model="search" prepend-inner-icon="search" :placeholder="t('Search')" density="compact" variant="outlined" hide-details clearable />
						<v-chip v-if="activeFilter" class="points-page__offer-filter" color="primary" variant="tonal" size="small" closable @click:close="clearOfferFilter">
							<md-icon>storefront</md-icon>
							{{ activeFilterLabel }}
						</v-chip>
						<div class="points-page__filter-chips">
							<v-chip v-for="opt in statusOptions" :key="opt.value" :color="statusFilters.includes(opt.value) ? opt.color : 'default'" :variant="statusFilters.includes(opt.value) ? 'flat' : 'outlined'" size="small" @click="toggleStatusFilter(opt.value)">{{ opt.label }}</v-chip>
						</div>
					</section>

					<section class="points-page__history">
						<h2 class="points-page__history-title">{{ t('PointsHistory') }}</h2>

						<div class="points-page__history-list">
							<div v-if="visibleHistory.length === 0" class="points-page__empty">{{ t('PointsHistoryEmpty') }}</div>

							<v-card v-for="entry in visibleHistory" :key="entry.id" :class="['points-page__entry', `points-page__entry--${statusClass(entry.status)}`]" variant="outlined" @click="onEntryClick(entry)">
								<div class="points-page__entry-row">
									<div class="points-page__entry-main">
										<div class="points-page__entry-title">{{ entry.promoName }}</div>
										<!-- Type chip hidden for "Auto" points (to re-enable later: restore v-if="entry.type") -->
										<v-chip v-if="entry.type && entry.type !== 'AUTO'" class="points-page__entry-type" size="x-small" variant="tonal" color="default">{{ typeLabel(entry.type) }}</v-chip>
										<div class="points-page__entry-meta">
											<span>
												<md-icon>calendar_today</md-icon>
												{{ formatDate(entry.date) }}
											</span>
											<span v-if="entry.location">
												<md-icon>location_on</md-icon>
												{{ entry.location }}
											</span>
											<span v-if="entry.validator">
												<md-icon>person</md-icon>
												{{ entry.validator }}
											</span>
										</div>
									</div>
									<div class="points-page__entry-right">
										<div class="points-page__entry-pts">
											<span :class="['points-page__entry-pts-final', entry.status === 'DENIED' ? 'points-page__entry-pts-final--denied' : '']">{{ entry.pointsValidated ?? entry.pointsClaimed }} {{ t('pts') }}</span>
											<span v-if="showInitialPoints(entry)" class="points-page__entry-pts-initial">{{ entry.pointsClaimed }}</span>
										</div>
										<v-chip :color="statusColor(entry.status)" size="x-small" variant="tonal">
											<md-icon v-if="entry.status === 'PENDING'">hourglass_empty</md-icon>
											<md-icon v-else-if="entry.status === 'VALIDATED'">check</md-icon>
											<md-icon v-else-if="entry.status === 'DENIED'">close</md-icon>
											<md-icon v-else>emoji_events</md-icon>
											{{ statusLabel(entry.status) }}
										</v-chip>
									</div>
								</div>

								<v-alert v-if="entry.status === 'DENIED' && entry.deniedReason" type="error" variant="tonal" density="compact" :title="t('DeniedReason')" class="points-page__entry-denied">
									{{ entry.deniedReason }}
								</v-alert>
							</v-card>

							<v-btn v-if="canLoadMore" variant="text" block class="points-page__more" @click="loadMore">{{ t('LoadMore') }}</v-btn>
						</div>
					</section>
				</template>
			</v-card-text>

			<v-card-actions>
				<v-spacer></v-spacer>
				<v-btn text @click="open = false" data-close>{{ t('Close') }}</v-btn>
			</v-card-actions>

			<PointsEditDialog v-model="editOpen" :entry="editingEntry" :overlay-target="overlayTarget" @updated="onEntryUpdated" />
		</v-card>
	</v-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { getUserPoints, getPointsHistory, matchesOfferContext } from '@/api/sales/points';
import { useTranslator } from '@/utils/useTranslator';
import PointsEditDialog from './PointsEditDialog.vue';

const props = defineProps({
	modelValue: { type: Boolean, default: false },
	overlayTarget: { type: Object, default: null },
	filter: { type: Object, default: null },
});

const emit = defineEmits(['update:modelValue']);

const t = useTranslator();
const PAGE_SIZE = 10;

const open = ref(props.modelValue);
const loading = ref(false);
const user = ref({});
const history = ref([]);
const pageSize = ref(PAGE_SIZE);
const search = ref('');
const statusFilters = ref([]);
const editOpen = ref(false);
const editingEntry = ref(null);
const activeFilter = ref(null);

const activeFilterLabel = computed(() => {
	if (!activeFilter.value) return '';
	const match = history.value.find((e) => matchesOfferContext(e, activeFilter.value));
	const parts = [match?.promoName, match?.location].filter(Boolean);
	return parts.length ? parts.join(' · ') : t('PointsOnThisOffer');
});

function clearOfferFilter() {
	activeFilter.value = null;
}

const statusOptions = computed(() => [
	{ value: 'PENDING', label: t('Pending'), color: 'warning' },
	{ value: 'VALIDATED', label: t('Validated'), color: 'success' },
	{ value: 'DENIED', label: t('Denied'), color: 'error' },
	{ value: 'AUTO', label: t('Auto'), color: 'info' },
]);

const filteredHistory = computed(() => {
	const q = search.value?.trim().toLowerCase();
	return history.value.filter((e) => {
		if (!matchesOfferContext(e, activeFilter.value)) return false;
		if (statusFilters.value.length && !statusFilters.value.includes(e.status)) return false;
		if (q && !(e.promoName?.toLowerCase().includes(q) || e.location?.toLowerCase().includes(q))) return false;
		return true;
	});
});

const visibleHistory = computed(() => filteredHistory.value.slice(0, pageSize.value));
const canLoadMore = computed(() => filteredHistory.value.length > pageSize.value);

watch(
	() => props.modelValue,
	async (v) => {
		open.value = v;
		if (v) {
			activeFilter.value = props.filter ?? null;
			await load();
		}
	}
);

watch(open, (v) => emit('update:modelValue', v));

async function load() {
	loading.value = true;
	try {
		const [u, h] = await Promise.all([getUserPoints(true), getPointsHistory(true)]);
		user.value = u ?? {};
		history.value = (h ?? []).sort((a, b) => new Date(b.date) - new Date(a.date));
		window.dispatchEvent(new CustomEvent('points-updated', { detail: { total: user.value.total ?? 0 } }));
	} finally {
		loading.value = false;
	}
}

function loadMore() {
	pageSize.value += PAGE_SIZE;
}

function toggleStatusFilter(v) {
	const i = statusFilters.value.indexOf(v);
	if (i >= 0) statusFilters.value.splice(i, 1);
	else statusFilters.value.push(v);
	pageSize.value = PAGE_SIZE;
}

function onEntryClick(entry) {
	if (entry.status !== 'PENDING') return;
	editingEntry.value = entry;
	editOpen.value = true;
}

async function onEntryUpdated() {
	await load();
}

function showInitialPoints(entry) {
	if (entry.pointsValidated == null) return false;
	return entry.pointsValidated !== entry.pointsClaimed;
}

function statusClass(status) {
	if (status === 'VALIDATED') return 'validated';
	if (status === 'DENIED') return 'denied';
	if (status === 'AUTO') return 'auto';
	return 'pending';
}

function statusColor(status) {
	if (status === 'VALIDATED') return 'success';
	if (status === 'DENIED') return 'error';
	if (status === 'AUTO') return 'info';
	return 'warning';
}

function statusLabel(status) {
	if (status === 'VALIDATED') return t('Validated');
	if (status === 'DENIED') return t('Denied');
	if (status === 'AUTO') return t('Auto');
	return t('Pending');
}

function typeLabel(type) {
	if (type === 'NEGOTIATION') return t('Negotiation');
	if (type === 'BONUS') return t('Bonus');
	if (type === 'AUTO') return t('Auto');
	return type ?? '';
}

function tierClass(tier) {
	const k = (tier ?? '').toLowerCase();
	return `points-page__tier--${k}`;
}

function formatNumber(n) {
	if (n == null) return '0';
	return new Intl.NumberFormat('fr-FR').format(n);
}

function formatDate(iso) {
	if (!iso) return '';
	const d = new Date(iso);
	const sameYear = d.getFullYear() === new Date().getFullYear();
	const opts = sameYear ? { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' } : { day: '2-digit', month: '2-digit', year: 'numeric' };
	return d.toLocaleString('fr-FR', opts).replace(':', 'h');
}
</script>
